import {currentRegistry, type PricingFacts} from '../sports/registry';
import {ClubPlanFeatures, ClubSubscriptionTier, ClubSubscriptionTierLabels} from "@/constants";

/**
 * THE source of truth for what each model-2 plan is called, costs, and includes.
 *
 * This file exists because plan copy had drifted into at least three places — the pricing page,
 * ClubTierUpgradeModal and ClubSubscriptionTier/ClubPlanFeatures — and drift has already cost us
 * twice: a stale /pricing-v2 quoting prices that no longer existed, and a transaction rate written
 * out by hand in two files that disagreed. Feature bullets had gone the same way: the upgrade modal
 * was still selling "unlimited active events" and "unlimited players on cash events", limits that
 * no longer exist, and never mentioned Match Centre, Leagues or Club Players — the actual reasons
 * to upgrade.
 *
 * Anything user-facing about a plan belongs here. Prices are the one exception and deliberately so:
 * the amounts come from the pricing API and the per-country rates from PlatformFeeService, because
 * those are the server's to state.
 *
 * LEGACY (pricing_model = 1) IS NOT DESCRIBED HERE. Legacy clubs keep the old tiers, the old
 * feature map and the old names; `featuresForTier` routes them back to ClubPlanFeatures. Rewriting
 * their plan copy to match this model would tell existing customers they have something they don't.
 */

/** Keys of PlatformFeeService's per-country `plan_rates` — offset from the enum names by one. */
export type RateKey = 'payg' | 'starter' | 'club' | 'pro';

/**
 * Tier ids as literals, NOT `ClubSubscriptionTier.Free` and friends.
 *
 * `constants.ts` imports SPORTS_LIST from `user/_components/useEntityEditor.tsx`, which pulls a large
 * component graph back round to constants — a genuine import cycle. Every existing consumer gets
 * away with it because they read the enum from inside a component body, by which time the module has
 * initialised. PLAN_META is a module-level array, so reading the enum here evaluated it mid-cycle and
 * `ClubSubscriptionTier` was still undefined: the whole app 500'd on any route that could reach the
 * upgrade modal. Typechecking passed, because a cycle is a runtime fact.
 *
 * TIER_IDS_MATCH_ENUM below fails loudly in dev if these ever drift from the enum.
 */
const TIER_FREE = 100;
const TIER_BASIC = 150;   // sells as "Starter", $19
export const TIER_PLUS = 200;    // ClubSubscriptionTier.Starter — sells as "Plus", $39
const TIER_PRO = 300;

export type PlanMeta = {
    /** The enum case this plan IS. Note the offset: Basic = "Starter" $19, Starter = "Plus" $39. */
    tier: number;
    name: string;
    rateKey: RateKey;
    /** Used only until the per-country schedule resolves; a mismatch is reported in dev. */
    fallbackOffset: number;
    sub: number;
    popular: boolean;
    primary: boolean;
    ctaTo: string;
    cta: string;
    forText: string;
    headLabel: string;
    features: string[];
    rateNote?: string;
    capFeature?: string;
};

export const PLAN_META: PlanMeta[] = [
    {
        tier: TIER_FREE, name: 'Free', rateKey: 'payg', fallbackOffset: 0, sub: 0,
        popular: false, primary: false, ctaTo: "/signup/player/", cta: "Start free",
        forText: "Run your events, take registrations online, pay nothing.", headLabel: "Includes",
        features: [
            "Run unlimited events with online payments",
            "All formats — brackets, round robins, Americano, Mexicano & King of the Court",
            "Draws, Games, live scores, standings & player ratings",
            "DUPR rating submission (pickleball)",
            "Free public club page",
        ],
        capFeature: "Cash or free-entry events need an Event Pass",
    },
    {
        tier: TIER_BASIC, name: 'Starter', rateKey: 'starter', fallbackOffset: 0, sub: 19,
        popular: false, primary: false, ctaTo: "/signup/player/", cta: "Start your free trial",
        forText: "For clubs running leagues and match days.", headLabel: "Everything in Free, plus",
        features: [
            "No Event Passes needed — cash and free-entry events included",
            "Leagues Access",
            "Match Centre Lite, includes check-in",
            "Club Players CRM Lite — player history",
        ],
    },
    {
        tier: TIER_PLUS, name: 'Plus', rateKey: 'club', fallbackOffset: 1, sub: 39,
        popular: true, primary: true, ctaTo: "/signup/player/", cta: "Start your free trial",
        forText: "For your club, not one event.", headLabel: "Plus is for your club",
        // EXACTLY the club features of the one Plus sentence (PRICING_COPY.plusClubFeatures):
        // the pricing page, the billing page and the go-live modal all draw this list. Event-level
        // features (live scores, the desk on the day, the PDFs) belong to the Event Pass.
        features: ['Player CRM', 'Club-wide leaderboards', 'Dashboards', 'Branding on every event', 'No ads', 'Unlimited events'],
    },
    {
        // Pro sells by conversation, not self-serve checkout — hence /contact/. Its copy is the
        // plan as it was last published; the fee calculator has always compared Plus against it.
        tier: TIER_PRO, name: 'Pro', rateKey: 'pro', fallbackOffset: 2, sub: 129,
        popular: false, primary: false, ctaTo: "/contact/", cta: "Talk to us",
        forText: "For high-volume directors and venues running large draws.", headLabel: "Everything in Plus, plus",
        features: [
            "Lowest transaction rate",
            "Auto-scheduling & automatic waitlist promotion",
            "TV Mode for venue screens",
            "Gamified leaderboards",
            "Analytics dashboards",
            "Priority onboarding & dedicated account manager",
        ],
    },
];

/**
 * The plan-rate key for a tier.
 *
 * Kept as a function rather than written out at each call site because the enum/key offset is the
 * single most repeated mistake in this area: the upgrade modal was quoting a $19 club the Pro rate
 * on one line and the pay-as-you-go rate on another.
 */
/**
 * The one-off organizer path's Event Pass, as it is described to organizers. Prices are NOT here —
 * they come from the gate payload / event-pass/status, which is the server's single resolver, so
 * what is shown is what Stripe charges. Only the words live here, so no modal or card invents
 * its own description of the same thing.
 */
export const ONE_OFF_PASS = {
    name: 'Event Pass',
    /** What the pass is, in one line. */
    what: 'One payment, sized by your entry count, that covers this one event from the first score through to the final.',
    /** The free band, with the number filled in by the caller. */
    freeUpTo: (n: number) => n > 0
        ? `Events with ${n} or fewer entries are free from start to finish — every time.`
        : 'One pass covers this event from the first score through to the final, whatever its size.',
    /** Online payment changes what counts: only the entries the organizer adds themselves. */
    onlineAlternative: 'Take entry fees online through ScorePal and only the entries you add yourself count towards the pass.',
    /** The third route: fees are on with a real amount, but payout setup was never finished. */
    onboardingAlternative: 'This event is set to take entry fees online — finish your payment setup and self-registered entries stop counting.',
    onboardingTitle: 'Finish payment setup',
    onboardingBody: 'Your payout account is not complete yet, so online payment is not live and every entry still counts. It takes a couple of minutes.',
    /** The earn route. */
    earnTitle: 'Or get your players registered — free',
    earnBody: (required: number, total: number) =>
        `Once ${required} of your ${total} players have registered and verified their phone, this event unlocks free — no payment at all.`,
    topupTitle: 'This event has outgrown its pass',
    topupBody: 'Adding this entry takes the field into the next band. Pay the difference and it goes straight in — scores are never interrupted.',
    liveTitle: 'This event needs an Event Pass to go live',
    liveBody: "Your draw is ready and your event has outgrown the free band. Unlock your Event Pass before the first score goes in to give your players the full ScorePal experience: live scores, automatic standings, and a professional event that's effortless to run.",
    /**
     * The price spread across the field, appended to liveBody: "With 18 players, that's just $0.56
     * each." `count`/`unit` come from the server (OneOffPathService::headcount — pairs count as two
     * players, team sports as teams); `each` is already formatted. Rounded UP to the cent by the
     * caller, so the line never promises less than the pass costs.
     */
    perHead: (count: number, unit: 'players' | 'teams', each: string) =>
        `With ${count} ${count === 1 ? unit.slice(0, -1) : unit}, that's just ${each} each.`,
    /** Esports: Swiss or double elimination was chosen on an event that holds no pass. */
    formatTitle: 'Swiss and double elimination come with the Event Pass',
    formatBody: (price: string | null) =>
        `${price ? `${price} one-time` : 'One payment'} for this event. It also opens the Match Centre and your Branding Kit. A plain knockout or groups stay free.`,
};

/**
 * The go-live gate (golive_gate_enabled): the price is SHOWN at the draw, never blocking, and
 * ENFORCED at the first live action above the cap — open registration, go live, the PDF, a score.
 * The numbers (cap, price, plans) always come from the server's payload; only words live here.
 */
/** The production figures, used until GET /sports has answered (the registry carries the live ones). */
export const PRICING_FACTS_FALLBACK: PricingFacts = {
    free_entries_cap:   {tournament: 8, league: 4},
    event_pass_tiers:   [{max_entries: 16, cents: 999}, {max_entries: 32, cents: 1999}, {max_entries: null, cents: 2999}],
    annual_pass_cents:  7900,
    plus_monthly_cents: 3900,
    trial_days:         7,
    golive_gate:        false,
    event_pass_waived:  false,
    free_score_entries: 2,
    // Payments v3 and the Plus trial, as the registry snapshot carries them (config/payments.php).
    payments_v3:        {
        enabled: false,
        all_in_fees: {GBP: {rate: 3, fixed_cents: 40, plus_rate: 2}, EUR: {rate: 3, fixed_cents: 40, plus_rate: 2}, USD: {rate: 4.5, fixed_cents: 80, plus_rate: 3.5}, AUD: {rate: 5, fixed_cents: 60, plus_rate: 4}, CAD: {rate: 4.5, fixed_cents: 80, plus_rate: 3.5}},
        fallback_currency: 'USD', plus_discount_points: 1,
        express_countries: ['GB', 'US', 'IE', 'AU', 'CA', 'NZ', 'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'],
        refund_keeps_fee: true,
    },
    plus_trial:         {days: 14, fee_cents: 299, primary: true, monthly_cents: 3900, enabled: false},
    pass_offer:         {enabled: false, percent: 50, hours: 72, delay_hours: 2, reminder_hours: 48},
    // Trial v3, as the registry snapshot carries it (registry.fallback.json).
    trial_v3:           false,
    trial_hours:        48,
    league_trial_matchdays: 3,
    league_trial_days_cap:  21,
};

/** The caps, tiers, plans and trial length the product runs on — from the registry, else the fallback. */
export function pricingFacts(): PricingFacts {
    return currentRegistry().pricing ?? PRICING_FACTS_FALLBACK;
}

/** "$9.99", "$79": the gate's prices are USD. */
export function usd(cents: number): string {
    const n = cents / 100;
    return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/**
 * Every sentence the site and the app say about the gate, built from the facts above and nowhere
 * else: the pricing page, the FAQ, the communities page, the trial toast, the sidebar, the quiz.
 */
export const PRICING_COPY = {
    /** "Tournaments run live free up to 8 teams, leagues up to 4." — or, with the Event Pass waived, free at any size. */
    freeRun: (f: PricingFacts = pricingFacts()) => f.event_pass_waived
        ? 'Tournaments and leagues run live free, whatever their size.'
        : `Tournaments run live free up to ${f.free_entries_cap.tournament} teams, leagues up to ${f.free_entries_cap.league}.`,
    /** The Plus trial alone, with the length from TrialService — in hours and matchdays under trial v3. */
    plusTrialLine: (f: PricingFacts = pricingFacts()) => f.trial_v3
        ? `Plus features free for ${f.trial_hours ?? 48} hours on a one-off event, and for a league's first ${f.league_trial_matchdays ?? 3} matchdays.`
        : `Plus features free for ${f.trial_days} days.`,
    /** The trial sentence followed by what runs free. */
    trialLine: (f: PricingFacts = pricingFacts()) => `${PRICING_COPY.plusTrialLine(f)} ${PRICING_COPY.freeRun(f)}`,
    /** "$9.99 up to 16 entries · $19.99 for 17–32 · $29.99 above" */
    tierLine: (f: PricingFacts = pricingFacts()) => f.event_pass_tiers.map((t, i) => {
        if (i === 0) return `${usd(t.cents)} up to ${t.max_entries} entries`;
        if (t.max_entries === null) return `${usd(t.cents)} above`;
        return `${usd(t.cents)} for ${(f.event_pass_tiers[i - 1].max_entries ?? 0) + 1}–${t.max_entries}`;
    }).join(' · '),
    /** The Event Pass rule in one sentence — or, with the pass waived, the fact that there is none. */
    eventPassRule: (f: PricingFacts = pricingFacts()) => f.event_pass_waived
        ? 'Every tournament and league runs free, whatever its size. The plans add the Plus features.'
        : `Free up to ${f.free_entries_cap.tournament} teams (${f.free_entries_cap.league} for a league). Above that, one pass per event: ${PRICING_COPY.tierLine(f)}.`,
    /**
     * The in-app trial toast under the gate: what is free and until when, in words an organiser
     * does not have to decode. No matchdays, no brackets, and nothing about the Event Pass — that
     * is a different message, on the event page. The body names the features (plusTrialBody).
     */
    gateTrialTitle: (days: number) => days > 0 ? `Plus club features are free for ${days} more day${days === 1 ? '' : 's'}` : 'Plus club features are free for now',
    /**
     * Trial v3: the one-off route counts down in hours; the league route says the date it ends
     * (`endsOn`, formatted by the caller from the trial's expiry), which the engine moves as
     * matchdays are played — the organiser only ever needs the date.
     */
    v3TrialTitle: (v3: {rule: string; hours_left: number; trial_hours: number; matchdays_completed: number; matchdays_total: number}, days: number, endsOn: string | null = null) => {
        if (v3.rule === 'one_off') {
            if (v3.hours_left <= 0) return 'Plus club features are free for now';
            if (v3.hours_left < v3.trial_hours || v3.hours_left <= 48) return `Plus club features are free for the next ${v3.hours_left} hour${v3.hours_left === 1 ? '' : 's'}`;
            return PRICING_COPY.gateTrialTitle(days);
        }
        if (v3.rule === 'league') {
            return endsOn ? `Plus club features are free until ${endsOn}` : PRICING_COPY.gateTrialTitle(days);
        }
        return PRICING_COPY.gateTrialTitle(days);
    },
    /** The trial toast's one line under the title: what Plus is, and that running events is untouched. */
    plusTrialBody: 'That is player CRM, club-wide leaderboards, dashboards and branding. Running your events is not affected.',
    plusTrialCta: "What's in Plus",
    gateTrialBody: (f: PricingFacts = pricingFacts()) =>
        `Tournaments run live free up to ${f.free_entries_cap.tournament} teams, leagues up to ${f.free_entries_cap.league}; above that an Event Pass, asked for when you go live.`,
    /** The sidebar's button for a Free club under the gate. */
    annualPassLabel: (f: PricingFacts = pricingFacts()) => `Annual Pass — ${usd(f.annual_pass_cents)}/year`,

    // ---- what the pass covers, what Plus is for: ONE sentence, everywhere it is said ----------
    /** What an Event Pass runs: the event, start to finish. */
    eventPassFeatures: ['Live scores', 'Match Centre on the day', 'The PDFs', 'Results report'] as readonly string[],
    /** What Plus is for: the club. These ARE the Plus card's bullets, wherever a Plus card is drawn. */
    plusClubFeatures: ['Player CRM', 'Club-wide leaderboards', 'Dashboards', 'Branding on every event', 'No ads', 'Unlimited events'] as readonly string[],
    /** The club features as a clause: "player CRM, club-wide leaderboards, dashboards and branding". */
    plusFeaturesLine: 'player CRM, club-wide leaderboards, dashboards and branding',
    /** The sentence itself, the price from the facts. */
    plusSentence: (f: PricingFacts = pricingFacts()) =>
        `An Event Pass runs one event start to finish — live scores, Match Centre on the day, the PDFs. Plus is for your club: player CRM, club-wide leaderboards, dashboards, branding on every event, no ads, and unlimited events — ${usd(f.plus_monthly_cents)}/month.`,
    /** The emails' button. */
    keepWithPlus: (f: PricingFacts = pricingFacts()) => `Keep them with Plus — ${usd(f.plus_monthly_cents)}/month`,
    /** The quiz splash when an ad named the sport. */
    quizSplash: (sport: string, format: string, f: PricingFacts = pricingFacts()) =>
        `Build your ${sport} ${format} in 30 seconds — free up to ${f.free_entries_cap.tournament} teams (${f.free_entries_cap.league} for a league)`,
};

/**
 * The Event Pass follow-up email (PassFollowups): its Continue button lands on the event with
 * `?pass=open&src=pass_followup_email` — the banner opens the pass modal, the modal repeats the
 * guarantee the email made, and the checkout carries `src` into Stripe metadata so the purchase is
 * attributed to the email. Nothing is recorded on arrival (mail scanners open every link).
 */
export const PASS_FOLLOWUP = {
    src:            'pass_followup_email',
    openParam:      'pass',
    guaranteeTitle: 'Money-back guarantee:',
    guaranteeBody:  "if ScorePal doesn't work for your event, just reply to Shaun's email and we'll refund you in full.",
};

export const GO_LIVE_GATE = {
    title: (entries: number, unit: string, cap: number) => `${entries} ${unit} — above the free ${cap}`,
    sub: (cap: number, league: boolean) => league
        ? `Leagues with ${cap} or fewer entries run free start to finish. Above that, going live needs the sized league pass.`
        : `Events with ${cap} or fewer entries run free start to finish. Above that, running it live needs an Event Pass.`,
    body: 'Your draw and your registrations are free either way. The pass covers this one event from the first live moment through to the final — going live, the PDFs and every score.',
    previewTitle: 'Round one of your draw',
    passName: 'Event Pass',
    passWhat: (league: boolean) => league
        ? 'One payment for this league season, sized by its entry count.'
        : 'One payment for this event, sized by its entry count.',
    annualPassTitle: 'Annual Pass',
    annualPassBody: 'Unlimited events — every tournament and league you run, for a year.',
    annualPassHref: '/pricing/?plan=annual',
    plusTitle: 'Plus',
    plusBody: 'For your club: player CRM, club-wide leaderboards, dashboards, branding on every event, no ads, unlimited events.',
    plusHref: '/pricing/',
    buy: 'Get the Event Pass',
    later: 'Not now',
    /** The non-blocking banner at the draw. */
    bannerTitle: (entries: number, unit: string, cap: number, price: string) => `${entries} ${unit} — above ${cap}, running this live needs an Event Pass (${price}).`,
    bannerBody: 'Your draw and your registrations are free either way. The pass is asked for the first time you go live, download the PDF or enter a score.',
    bannerCta: 'See the pass',
    /**
     * Two free results before the pass. The banner's title moves with the count the server sends
     * (`free_scores` on the offer); with no allowance it is the plain bannerTitle. The modal at
     * the third result says so in its title; its body is unchanged.
     */
    freeScoresBanner: (entries: number, unit: string, cap: number, price: string, fs: {allowed: number; used: number} | null | undefined) => {
        if (!fs || fs.allowed < 1) return GO_LIVE_GATE.bannerTitle(entries, unit, cap, price);
        if (fs.used <= 0) return `${entries} ${unit} — above the free ${cap}. Enter your first ${fs.allowed} results free to see it work; the Event Pass (${price}) runs the rest.`;
        if (fs.used < fs.allowed) return `${fs.used} of ${fs.allowed} free results used · the pass runs the rest.`;
        return `Your ${fs.allowed} free results are in — the Event Pass (${price}) runs the rest of the event.`;
    },
    freeScoresBannerBody: 'Your draw and your registrations are free either way. The pass is asked for at the next result after the free ones.',
    seenItWorkTitle: "You've seen it work — the pass runs the rest.",
    /** One rule for every event, tournament or league: the free results, then the pass — no clock. */
    modalTitle: (entries: number, unit: string, cap: number, fs: {allowed: number; used: number} | null | undefined) =>
        fs && fs.allowed > 0 && fs.used >= fs.allowed ? GO_LIVE_GATE.seenItWorkTitle : GO_LIVE_GATE.title(entries, unit, cap),
    /** The create screen, with the gate on: online payment only waives the pass once someone has paid. */
    paidEventNote: 'Free when players pay online. Needs the first paid registration before the event goes live.',
    /** A held pass (earned, granted, bought ahead) can go on the event instead of a purchase. */
    usePass: 'Use your pass',
    usePassWhich: (label: string) => `Using your ${label} pass.`,
    /** A pass is on the event but covers less than the field: the difference is due before go-live. */
    attachedTopup: (topup: string) => `Pass attached — ${topup} top-up to go live`,
    attachedTopupBody: 'Your pass is on this event but covers a smaller size. Pay the difference and it runs live start to finish.',
    resumeTopup: 'Pay the top-up',
    /** Billing → Event Passes, under the gate. */
    billingPassesCopy: 'Hold a pass for your next event. Pick the size now; if the event ends up bigger, you pay the difference when you go live.',
    usePassDone: 'Your pass is on this event — it runs live start to finish.',
    /** The persistent strips the event page shows for the other states. */
    activeStrip: 'Event Pass active — this event runs live start to finish',
    coveredLine: (by: string | null | undefined) => by === 'annual_pass' ? 'Covered by your Annual Pass'
        : by === 'plus' ? 'Covered by Plus'
        : by === 'legacy' ? 'Covered by your plan'
        : by ? `Covered by ${by.charAt(0).toUpperCase()}${by.slice(1)}` : 'Covered by your plan',
    /** The locked desk, above the cap. */
    lockedTitle: (entries: number, unit: string, cap: number) => `${entries} ${unit} is above the free ${cap}`,
    lockedBody: 'Running this event live — the desk, the PDFs and scores — needs an Event Pass sized for it, the Annual Pass or Plus. Your draw and your registrations stay free.',
    lockedCta: 'See the Event Pass',
};

/**
 * The pass badge on an event card, from the server's `pass_state` (never recomputed here):
 * "Pass required · 10 teams", "Pass active", "Covered by plan", or nothing when free.
 */
export function passStateBadge(ps: {state: string; entries?: number; unit?: string} | null | undefined): {label: string; cls: string} | null {
    if (!ps) return null;
    switch (ps.state) {
        case 'required': {
            const unit = ps.entries === 1 ? (ps.unit ?? 'entries').slice(0, -1) : (ps.unit ?? 'entries');
            return {label: `Pass required · ${ps.entries ?? '?'} ${unit}`, cls: 'bg-[#FFFCEC] text-[#9a7d00] border border-[#E6C200]'};
        }
        case 'topup_due': {
            const due = ((ps as any).topup_due_cents ?? 0) / 100;
            let money: string;
            try {
                money = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(due);
            } catch {
                money = `$${due.toFixed(2)}`;
            }
            return {label: `Pass attached · ${money} top-up`, cls: 'bg-[#FFFCEC] text-[#9a7d00] border border-[#E6C200]'};
        }
        case 'active':  return {label: 'Pass active', cls: 'bg-green-100 text-green-700'};
        case 'covered': return {label: 'Covered by plan', cls: 'bg-default-100 text-default-600'};
        default:        return null;
    }
}

/**
 * The one question a new community club is asked before its first event. About what they need
 * to run, never about price: the answer decides which organizer path the club is on, once.
 */
export const ORGANIZER_PATH_QUESTION = {
    title: 'What are you organising?',
    sub: 'One quick question so we set things up the right way.',
    oneOffTitle: 'A one-off event',
    oneOffBody: 'A single tournament or social',
    communityTitle: 'Regular events for my community',
    communityBody: 'Weekly or monthly play, leagues, a growing group',
};

/**
 * Shown when a club set up for one-off events tries to create a season league (the server's
 * `community_path_required`). Plain words only — never "path": the organizer is told what a
 * league is and what the one button does.
 */
export const COMMUNITY_SWITCH = {
    title: 'Leagues run over a season',
    body: 'Leagues run over a season, so they work a little differently from one-off events. Switch your club to regular events to create one. Your existing events stay as they are.',
    cta: 'Switch and continue',
    error: 'Could not switch your club. Please try again.',
};

/**
 * The small print under the quiz's "Generate draw" button. One-off visitors are told the truth
 * about size before they build; everyone else keeps the line they always had.
 *
 * `ONE_OFF_FREE_ENTRIES_DISPLAY` mirrors the server's `pricing.one_off.free_entries` and exists
 * only because the quiz runs BEFORE there is an account to fetch the real number for. Anywhere
 * signed in, the number comes from the gate payload instead. Change the two together.
 */
export const ONE_OFF_FREE_ENTRIES_DISPLAY = 0;
export const QUIZ_ENTRY_NOTE = {
    // Under the go-live gate (GET /sports → pricing.golive_gate) the truth is the free cap and the
    // pass at go live. Before it, the trial comes first and the pass is what follows it. The days
    // come from the server (TrialService) either way — never a literal here.
    oneOff: (freeEntries: number = ONE_OFF_FREE_ENTRIES_DISPLAY, f: PricingFacts = pricingFacts()) => f.event_pass_waived
        // The Event Pass waived: free to run at any size, and only the Plus features are the trial's.
        ? `Free to run, whatever the size — no Event Pass. ${PRICING_COPY.plusTrialLine(f)}`
        : f.golive_gate
        ? `${PRICING_COPY.trialLine(f)} Above that, one Event Pass per event, asked for when you go live.`
        : freeEntries > 0
            ? `Free for ${f.trial_days} days from signup. After that, events over ${freeEntries} entries need an Event Pass before scoring starts.`
            : `Free for ${f.trial_days} days from signup. After that, each event needs an Event Pass before scoring starts.`,
    community: 'Free — no limit on entries.',
};

/**
 * What a pass buys, and how the event pages talk about it. The public live results link is FREE
 * for every event and is described that way here — it carries our ads and branding — so it is
 * deliberately not in `benefits`.
 */
export const ONE_OFF_PASS_BENEFITS = {
    /** The offer list, in the modal and the banner's modal. */
    benefits: [
        'Live scoring desk and Match Centre for the day',
        'Branding Kit — your logo, colours and sponsors on the event and live pages',
        'Score every match live, through to the final',
        'A pre-event PDF to hand to your group, and a results report after',
        'Set up the way you want it — your dedicated event organizer will help',
    ],
    /** Esports events only: the pass also opens the advanced formats for that one event. */
    esportsBenefit: 'Swiss and double elimination for this event',
    /** The list for THIS event: the esports line sits before the scoring line, racquet lists are unchanged in length. */
    benefitsFor(esports: boolean): string[] {
        return esports ? [this.benefits[0], this.benefits[1], this.esportsBenefit, ...this.benefits.slice(2)] : [...this.benefits];
    },
    /**
     * The two PDFs, shown as samples in the offer so the organizer can see what they get. Built by
     * `php artisan pdf:samples` through the real templates with made-up data — re-run it after a
     * template change. Static files in frontend/public/samples.
     */
    samplesTitle: 'See what you get',
    samples: [
        {key: 'pre_event', label: 'Pre-event PDF', hint: 'Fixtures, groups, rules', href: '/samples/scorepal-pre-event-sample.pdf'},
        {key: 'report', label: 'Results report', hint: 'Champion, tables, results', href: '/samples/scorepal-results-report-sample.pdf'},
    ],
    /**
     * A real public live page, the first tile of the strip: what their players and spectators will
     * follow on the day. The same two events the landing page already shows off (FeatureBlocks) —
     * a league offer gets the league, everything else the tournament.
     */
    liveExample: {
        key: 'live', label: 'Live scoring', hint: 'The public page your players follow', cta: 'See it live',
        tournament: 'https://scorepal.co/live/tournaments/scorepal-tournament-2026-4/',
        league:     'https://scorepal.co/live/leagues/scorepal-2026-league/',
    },
    /**
     * The promises that take the risk out of buying. Each is a commitment, not decoration:
     *   - `later`: a sized pass is bound to its event at purchase and has no expiry (EventPass
     *     carries none), so buying ahead of the day costs nothing.
     *   - `refund`: a POLICY, honoured by hand — there is no automated refund flow. Anyone changing
     *     the wording changes what we owe; keep the terms page in step.
     */
    reassurances: [
        {key: 'later', icon: 'material-symbols-light:event-available-outline', text: 'Use it now or later — the pass stays with this event and never expires.'},
        {key: 'refund', icon: 'material-symbols-light:verified-user-outline', text: "Event doesn't go ahead? Get your money back within 14 days."},
    ],
    bannerTitle: 'This event needs an Event Pass before scoring starts',
    /**
     * Names exactly what the pass opens. Esports leads with the formats; racquet adds the free route
     * only when it is real for this event (`earn.applies` on the offer — racquet sports only).
     */
    bannerBody: (price: string, earnApplies: boolean = true, esports: boolean = false) => esports
        ? `More entries than the free band. ${price} one-time. The pass opens Swiss and double elimination for this event, plus the Match Centre and your Branding Kit.`
        : `More entries than the free band. ${price} one-time. The pass opens the Match Centre and your Branding Kit for this event.`
            + (earnApplies ? ' Get your players registered and it unlocks free.' : ''),
    bannerCta: 'Unlock',
    unlockedTitle: 'Your event is unlocked',
    /** The one-line unlocked panel (the go-live gate), with a dismiss and nothing else. */
    unlockedLine: 'Your event is unlocked — pass active for this event',
    unlockedBody: 'Your pass opens these for this event:',
    unlockedMatchCentre: 'Open Match Centre — the live scoring desk',
    unlockedBranding: 'Add your logo and a sponsor',
    /** Esports events only. */
    unlockedFormats: 'Build a Swiss or double elimination draw',
    unlockedLiveLink: 'Copy your live results link',
    unlockedLiveLinkNote: 'Free for every event — spectators and the big screen follow along here.',
};

export const rateKeyForTier = (tier: number): RateKey =>
    tier >= TIER_PRO ? 'pro'
        : tier >= TIER_PLUS ? 'club'
            : tier >= TIER_BASIC ? 'starter'
                : 'payg';

/**
 * Dev-only check that the literals above still match the enum.
 *
 * Runs on first lookup rather than at import, which is the entire point: reading the enum at module
 * scope is what broke this file in the first place. By the time anything calls planForTier the cycle
 * has resolved and the enum is real.
 */
let tiersChecked = false;

function assertTierIdsMatchEnum(): void {
    if (tiersChecked || !import.meta.env.DEV) {
        return;
    }
    tiersChecked = true;

    const expected: [string, number, number][] = [
        ['Free', TIER_FREE, ClubSubscriptionTier.Free],
        ['Basic/Starter', TIER_BASIC, ClubSubscriptionTier.Basic],
        ['Starter/Plus', TIER_PLUS, ClubSubscriptionTier.Starter],
        ['Pro', TIER_PRO, ClubSubscriptionTier.Pro],
    ];

    for (const [name, literal, fromEnum] of expected) {
        if (literal !== fromEnum) {
            console.error(
                `[pricing/plans] tier id drift: ${name} is ${literal} here but ${fromEnum} in ClubSubscriptionTier. `
                + 'Plan lookups will silently return the wrong plan until these agree.',
            );
        }
    }
}

export const planForTier = (tier: number): PlanMeta | undefined => {
    assertTierIdsMatchEnum();

    return PLAN_META.find(p => p.tier === tier);
};

/**
 * What this tier is CALLED to this club.
 *
 * Model 2 says "Free"; the shared enum label still says "Pay-as-you-go", which is the legacy name
 * and reads as a different product from the one on /pricing. Legacy clubs keep their label.
 */
export function tierLabel(tier: number, isLegacy = false): string {
    if (isLegacy) {
        return ClubSubscriptionTierLabels[tier as keyof typeof ClubSubscriptionTierLabels];
    }

    return planForTier(tier)?.name
        ?? ClubSubscriptionTierLabels[tier as keyof typeof ClubSubscriptionTierLabels];
}

/** The feature bullets for a tier — model-2 copy from above, legacy copy from the old map. */
export function featuresForTier(tier: number, isLegacy = false): string[] {
    if (isLegacy) {
        return ClubPlanFeatures[tier as keyof typeof ClubPlanFeatures] ?? [];
    }

    return planForTier(tier)?.features
        ?? ClubPlanFeatures[tier as keyof typeof ClubPlanFeatures]
        ?? [];
}
